import { motion } from "framer-motion";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactSection() {
  const { t, locale } = useI18n();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, locale });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-[#27272A] focus:border-[#E1FF00] px-0 py-3 text-[#F4F4F5] placeholder:text-[#52525B] transition-colors";

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-[#050505] border-t border-[#27272A]" data-testid="contact-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid md:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="md:col-span-5"
        >
          <div className="flex items-center gap-3 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#A1A1AA]">
            <span className="w-8 h-px bg-[#E1FF00]" />
            {t.contact.overline}
          </div>
          <h2 className="mt-6 font-display text-5xl md:text-6xl text-[#F4F4F5] tracking-tighter leading-[0.95]">
            {t.contact.title}
          </h2>
          <p className="mt-4 text-[#A1A1AA] text-base md:text-lg">{t.contact.subtitle}</p>

          <div className="mt-10 space-y-6 pt-8 border-t border-[#27272A]">
            <div>
              <div className="font-mono-acc text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">Email</div>
              <a href="mailto:entretienproinc@gmail.com" className="mt-1 block font-display text-2xl text-[#F4F4F5] hover:text-[#E1FF00] transition-colors">
                entretienproinc@gmail.com
              </a>
            </div>
            <div>
              <div className="font-mono-acc text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">Téléphone</div>
              <a href="tel:+15144511361" className="mt-1 block font-display text-2xl text-[#F4F4F5] hover:text-[#E1FF00] transition-colors">
                (514) 451-1361
              </a>
            </div>
            <div>
              <div className="font-mono-acc text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">Zone de service</div>
              <div className="mt-1 text-[#F4F4F5]">Grand Montréal · Rive-Sud · Rive-Nord · Laval</div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          onSubmit={submit}
          className="md:col-span-7 md:pl-8 md:border-l md:border-[#27272A]"
          data-testid="contact-form"
        >
          <div className="space-y-6">
            <input required placeholder={t.contact.name + " *"} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name" />
            <input required type="email" placeholder={t.contact.email + " *"} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
            <input placeholder={t.contact.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="contact-phone" />
            <textarea required rows={5} placeholder={t.contact.message + " *"} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls + " resize-none"} data-testid="contact-message" />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-8 inline-flex items-center gap-3 bg-[#E1FF00] hover:bg-[#CBE600] disabled:opacity-50 text-[#050505] px-6 py-4 font-mono-acc text-[11px] tracking-[0.15em] uppercase font-semibold transition-colors"
            data-testid="contact-submit"
          >
            {loading ? t.contact.submitting : t.contact.submit} <span>→</span>
          </button>
        </motion.form>
      </div>
    </section>
  );
}
